import React from 'react';

import {DividerProps, PageContent, Typography} from '@genestack/ui';
import {useLocation} from 'wouter';
import {FavoriteDocumentsSidebar} from './favorites-sidebar';
import {RecentDocumentsSidebar} from './recents-sidebar';
import {SearchDocumentsSidebar} from './search-sidebar';
import {LayoutContextValue} from '../../../../components/layout/page-layout/use-page-layout';
import {useSidebarTabs} from '../../../../hooks/use-sidebar-tabs';
import {useHandleFindUsages} from '../../common/sidebar-search-field/hooks/use-handle-find-usages';
import {GlobalVersionData} from '../../../../hooks/global-version/use-global-version-query';
import {ViewModeTab} from '../../../../components/layout/page-layout/layout-typings';
import {InvalidDocumentsSidebar} from './invalid-documents-sidebar';
import styles from './sidebar.module.css';
import {addGlobalVersionToUrl} from '../../../../hooks/use-search-params';
import {VersionSelect} from '../../../../hooks/global-version/version-select/version-select';
import {useSidebarSearchProps} from '../../common/sidebar-search-field/hooks/use-sidebar-search-props';

interface Props {
    tabSettings: ReturnType<typeof useSidebarTabs>;
    layoutSettings: LayoutContextValue;
    searchSidebarContext: ReturnType<typeof useSidebarSearchProps>;
    findUsagesProps: ReturnType<typeof useHandleFindUsages>;
    globalVersions: GlobalVersionData[];
    selectedGlobalVersion: GlobalVersionData;
    getDocumentLink: (documentId: string, nodeId?: string) => string;
    documentId?: string;
    revisionId?: string;
}

const FOOTER_START_GAP: DividerProps = {startGap: 1, endGap: 3, variant: 'stroke'};
const FOOTER_END_GAP: DividerProps = {startGap: 2, endGap: 1};

export const ViewModeDocumentsSidebar = (props: Props) => {
    const [pathname, navigate] = useLocation();

    const redirectToVersion = (globalVersionId: number) => {
        navigate(addGlobalVersionToUrl(pathname, globalVersionId));
    };

    const footer = (
        <PageContent startDividerProps={FOOTER_START_GAP} endDividerProps={FOOTER_END_GAP}>
            <div className={styles.globalVersion}>
                <Typography as="span" variant="section" className={styles.globalVersionLabel}>
                    BioKB global version:
                </Typography>

                <VersionSelect
                    value={props.selectedGlobalVersion.id}
                    onChangeValue={redirectToVersion}
                    globalVersions={props.globalVersions}
                />
            </div>
        </PageContent>
    );

    switch (props.tabSettings.activityBarTab) {
        default:
        case ViewModeTab.RECENT:
            return (
                <RecentDocumentsSidebar
                    getDocumentLink={props.getDocumentLink}
                    footer={footer}
                    closeSidebar={props.layoutSettings.closeSidebar}
                    selectedGlobalVersion={props.selectedGlobalVersion}
                    documentId={props.documentId}
                />
            );
        case ViewModeTab.SEARCH:
            return (
                <SearchDocumentsSidebar
                    layoutSettings={props.layoutSettings}
                    searchSidebarProps={props.searchSidebarContext}
                    findUsagesProps={props.findUsagesProps}
                    selectedGlobalVersion={props.selectedGlobalVersion}
                    getDocumentLink={props.getDocumentLink}
                    footer={footer}
                    closeSidebar={props.layoutSettings.closeSidebar}
                    documentId={props.documentId}
                    revisionId={props.revisionId}
                />
            );
        case ViewModeTab.FAVORITES:
            return (
                <FavoriteDocumentsSidebar
                    getDocumentLink={props.getDocumentLink}
                    footer={footer}
                    closeSidebar={props.layoutSettings.closeSidebar}
                    selectedGlobalVersion={props.selectedGlobalVersion}
                    documentId={props.documentId}
                />
            );
        case ViewModeTab.INVALID:
            return (
                <InvalidDocumentsSidebar
                    getDocumentLink={props.getDocumentLink}
                    selectedGlobalVersion={props.selectedGlobalVersion}
                    footer={footer}
                    closeSidebar={props.layoutSettings.closeSidebar}
                />
            );
    }
};
